/**
 * ProductCard.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { Link, useNavigate } from 'react-router-dom';
import { getWholesalePrice, getAvailableWholesaleTiers } from '../data/products';
import AsyncProductImage from './AsyncProductImage';
import { getIcon } from '../utils/icons';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './ProductCard.css';

const getCategoryIcon = (product) => {
  // Check if product has a category field (preferred)
  const categoryName = product.category || product.type;

  if (!categoryName) {
    return getIcon('bundle');
  }

  const category = categoryName.toLowerCase();

  // Map category names to icons - supports all database categories
  if (category === 'book' || category.includes('book')) {
    return '📖';
  } else if (category === 'cd' || category.includes('cd')) {
    return '💿';
  } else if (category === 'bundle' || category.includes('bundle')) {
    return '📦';
  } else if (category === 'video' || category.includes('video')) {
    return '🎬';
  } else if (category === 'mp3' || category.includes('mp3')) {
    return '🎵';
  } else if (category === 'coffee' || category.includes('coffee')) {
    return '☕';
  } else if (category === 'sheet music' || category.includes('sheet music')) {
    return '🎼';
  } else if (category === 'merchandise' || category.includes('merchandise')) {
    return '🎁';
  } else if (category === 'accessories' || category.includes('accessories') || category.includes('accessory')) {
    return '🎸';
  } else if (category === 'digital download' || category.includes('digital download') || category.includes('download')) {
    return '📥';
  } else if (category.includes('pipe music')) {
    return getIcon('music');
  } else if (category.includes('celtic music')) {
    return getIcon('music');
  } else if (category.includes('irish')) {
    return '🍀';
  } else if (category.includes('scottish')) {
    return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
  } else if (category.includes('canadian')) {
    return '🍁';
  } else if (category.includes('cape breton')) {
    return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
  }

  // Default fallback
  return '📦';
};

const ProductCard = ({ product, showInventory = true, showHiddenBadge = false }) => {
  const { addItem } = useCart();
  const { currentUser, getUserWholesaleTier } = useAuth();
  const { showSuccess, showError } = useToast();
  const { t } = useLanguage();
  const p = t.shop.product;
  const navigate = useNavigate();
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (showNotifyForm && currentUser?.email && !notifyEmail) {
      setNotifyEmail(currentUser.email);
    }
  }, [showNotifyForm, currentUser, notifyEmail]);

  // Early return if product is not available (after hooks)
  if (!product) {
    return <div className="product-card">{p.notAvailable}</div>;
  }

  const isOutOfStock = product.inventory === 0;
  const isWholesaler = currentUser?.isWholesaler;
  const wholesaleTier = getUserWholesaleTier();

  // Calculate pricing based on user type and quantity
  const retailPrice = product.price_cents ?? product.price;
  const wholesalePrice = isWholesaler ? getWholesalePrice(product, wholesaleTier, selectedQuantity) : retailPrice;
  const isWholesalePricing = wholesalePrice !== retailPrice;
  const savings = retailPrice - wholesalePrice;
  const savingsPercent = Math.round((savings / retailPrice) * 100);

  // Get available wholesale tiers for current quantity
  const availableTiers = isWholesaler ? getAvailableWholesaleTiers(product, selectedQuantity) : [];

  const handleQuantityChange = (e) => {
    setSelectedQuantity(parseInt(e.target.value));
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!notifyEmail || isSubscribing) return;

    setIsSubscribing(true);
    try {
      const response = await fetch(`/api/products/${product.id}/notify-in-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: notifyEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(true);
        showSuccess(data.message || p.notifyDefault);
        setNotifyEmail('');
        setTimeout(() => {
          setShowNotifyForm(false);
          setIsSubscribed(false);
        }, 3000);
      } else {
        showError(data.error || p.notifyFailed);
      }
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      showError(p.notifyFailedRetry);
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleAddToCart = () => {
    if (isAdding) return;

    if (selectedQuantity > product.inventory) {
      showError(fmt(p.onlyNInStock, { n: product.inventory }));
      return;
    }

    setIsAdding(true);

    try {
      const itemToAdd = {
        ...product,
        quantity: selectedQuantity,
        unitPrice: wholesalePrice,
        isWholesalePrice: isWholesalePricing,
        wholesaleTier: wholesaleTier
      };
      if (addItem(itemToAdd, selectedQuantity)) {
        const priceText = isWholesalePricing
          ? fmt(p.atWholesale, { price: (wholesalePrice / 100).toFixed(2) })
          : '';
        showSuccess(
          `✓ ${selectedQuantity > 1
            ? fmt(p.addedMany, { n: selectedQuantity, price: priceText })
            : fmt(p.addedOne, { price: priceText })}`
        );
        setSelectedQuantity(1);
      } else {
        showError(p.addFailed);
      }
    } catch (error) {
      showError(p.addFailedRetry);
    } finally {
      setTimeout(() => setIsAdding(false), 500);
    }
  };

  return (
    <div className={`product-card ${isOutOfStock ? 'out-of-stock' : ''} ${isWholesaler ? 'wholesale-view' : ''}`}>
      {(product.featured === 1 || product.featured === true) && (
        <div className="featured-badge" title={p.featured}>⭐</div>
      )}
      {isWholesaler && (
        <div className="wholesale-badge" title={p.wholesaleAvailable}>🏢</div>
      )}
      {showHiddenBadge && (product.hide_from_shop === 1 || product.hide_from_shop === true) && (
        <div className="hidden-shop-badge" title={p.hiddenFromShop}>🙈</div>
      )}
      <Link to={`/product/${product.id}`} className="product-image-link">
        <AsyncProductImage
          product={product}
          alt={product.name}
          className="product-image"
        />
      </Link>
      <Link to={`/product/${product.id}`} style={{ textDecoration: 'none' }}>
        <h3 className="product-name">
          <span className="category-icon" title={product.category || product.type || 'Bundle'}>{getCategoryIcon(product)}</span> {product.name}
        </h3>
      </Link>
      <p className="product-artist">
        {(product.category?.toLowerCase().includes('book') || product.type === 'book') ? product.author : product.artist}
      </p>
      <p className="product-description">{product.description}</p>


      {/* Pricing Section */}
      <div className="product-pricing">
        {(product.is_bundle === 1 || product.is_bundle === true) ? (
          <div className="bundle-pricing">
            <div className="product-price bundle-price">
              <span style={{ fontSize: '14px', color: '#666' }}>{p.price} </span>
              <strong>{p.variable}</strong>
            </div>
            {product.bundle_discount && (
              <div className="bundle-discount-badge" style={{
                marginTop: '5px',
                padding: '4px 8px',
                background: 'rgba(40, 167, 69, 0.1)',
                color: '#28a745',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {fmt(p.upToOff, { pct: product.bundle_discount })}
              </div>
            )}
          </div>
        ) : isWholesaler && product.wholesalePricing ? (
          <div className="wholesale-pricing">
            <div className="price-display">
              {isWholesalePricing ? (
                <>
                  <div className="wholesale-price">
                    ${(wholesalePrice / 100).toFixed(2)}
                    <span className="wholesale-tier-badge">{wholesaleTier}</span>
                  </div>
                  <div className="retail-price-crossed">
                    ${(retailPrice / 100).toFixed(2)}
                  </div>
                  <div className="savings-badge">
                    {fmt(p.savePct, { pct: savingsPercent })}
                  </div>
                </>
              ) : (
                <div className="retail-price">
                  ${(retailPrice / 100).toFixed(2)}
                  <span className="no-wholesale-text">{fmt(p.minWholesale, { n: product.wholesalePricing[wholesaleTier]?.minQuantity || 5 })}</span>
                </div>
              )}
            </div>

            {/* Quantity Selector for Wholesalers */}
            <div className="quantity-selector">
              <label htmlFor={`quantity-${product.id}`}>{p.quantity}</label>
              <select
                id={`quantity-${product.id}`}
                value={selectedQuantity}
                onChange={handleQuantityChange}
                className="quantity-select"
              >
                {[...Array(Math.min(product.inventory, 50))].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>

            {/* Wholesale Tier Information */}
            {availableTiers.length > 0 && (
              <div className="tier-info">
                <small>{p.availableDiscounts}</small>
                <div className="tier-badges">
                  {availableTiers.map((tier) => (
                    <span
                      key={tier.tier}
                      className={`tier-badge ${tier.tier === wholesaleTier ? 'active' : ''}`}
                    >
                      {fmt(p.tierOff, { tier: tier.tier, pct: tier.savingsPercent })}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="retail-pricing">
            <div className="product-price">${(retailPrice / 100).toFixed(2)}</div>
          </div>
        )}
      </div>

      <button
        className={`add-to-cart-btn ${isAdding ? 'adding' : ''}`}
        onClick={(product.is_bundle === 1 || product.is_bundle === true || product.has_variants === 1 || product.has_variants === true) ? () => navigate(`/product/${product.id}`) : handleAddToCart}
        disabled={isOutOfStock || isAdding}
      >
        {isOutOfStock ? `${getIcon('error')} ${p.outOfStock}` :
          isAdding ? `${getIcon('success')} ${p.adding}` :
            (product.is_bundle === 1 || product.is_bundle === true) ? `📦 ${p.selectBundle}` :
              (product.has_variants === 1 || product.has_variants === true) ? p.selectOptions :
                isWholesaler ? `${getIcon('add')} ${fmt(p.addQtyToCart, { n: selectedQuantity })}` : `${getIcon('add')} ${p.addToCart}`}
      </button>

      {/* Back-in-Stock Notification Form */}
      {isOutOfStock && (
        <div className="notify-in-stock-section">
          {!showNotifyForm && !isSubscribed ? (
            <button
              className="notify-btn"
              onClick={() => {
                // Pre-fill email if user is logged in
                if (currentUser?.email) {
                  setNotifyEmail(currentUser.email);
                }
                setShowNotifyForm(true);
              }}
              type="button"
            >
              🔔 {p.notifyMe}
            </button>
          ) : isSubscribed ? (
            <div className="notify-success">
              ✓ {p.notifySuccess}
            </div>
          ) : (
            <form className="notify-form" onSubmit={handleNotifySubmit}>
              <input
                type="email"
                className="notify-email-input"
                placeholder={p.notifyEmailPlaceholder}
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                required
                disabled={isSubscribing}
              />
              <div className="notify-form-actions">
                <button
                  type="submit"
                  className="notify-submit-btn"
                  disabled={isSubscribing || !notifyEmail}
                >
                  {isSubscribing ? p.subscribing : p.subscribe}
                </button>
                <button
                  type="button"
                  className="notify-cancel-btn"
                  onClick={() => {
                    setShowNotifyForm(false);
                    // Reset to user's email if logged in, otherwise empty
                    setNotifyEmail(currentUser?.email || '');
                  }}
                  disabled={isSubscribing}
                >
                  {p.cancel}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCard; 